import { api } from './api';

export type WiredStatus = 'pendente' | 'processando' | 'enviado' | 'erro' | 'cancelado';

export const WIRED_STATUS: WiredStatus[] = ['pendente', 'processando', 'enviado', 'erro', 'cancelado'];

export interface WiredConexao {
  id: string;
  nome: string;
  room_id: number;
  chave_leitura_mascarada: string;
  possui_chave_escrita: boolean;
  chave_escrita_mascarada: string | null;
  ativo: boolean;
  funcionalidades: string[];
  criado_em: string;
  alterado_em: string;
}

export interface WiredConexaoPayload {
  nome?: string;
  room_id?: number;
  chave_leitura?: string;
  chave_escrita?: string;
  ativo?: boolean;
}

export interface WiredTesteConexaoFuncionalidade {
  codigo: string;
  nome_variavel: string;
  variavel_existe: boolean;
}

export interface WiredTesteConexaoResposta {
  ok: boolean;
  erro?: string;
  quantidade_variaveis?: { usuario: number; mobilia: number; global: number };
  funcionalidades?: WiredTesteConexaoFuncionalidade[];
}

export interface WiredFuncionalidade {
  id: string;
  codigo: string;
  descricao: string;
  id_conexao: string | null;
  conexao_nome: string | null;
  nome_variavel: string | null;
  escopo: string;
  ativo: boolean;
  alterado_em: string;
}

export interface WiredFuncionalidadePayload {
  id_conexao?: string | null;
  nome_variavel?: string;
  ativo?: boolean;
}

export interface WiredSincronizacao {
  id: string;
  funcionalidade: string;
  funcionalidade_descricao: string;
  nick: string;
  valor: string | number | null;
  status: WiredStatus;
  tentativas: number;
  ultimo_erro: string | null;
  proxima_tentativa_em: string | null;
  enviado_em: string | null;
  responsavel_nick: string | null;
  criado_em: string;
  alterado_em: string;
}

export type WiredResumo = Record<WiredStatus, number>;

export type WiredHudFormato = 'numero' | 'texto_por_valor' | 'booleano' | 'tempo' | 'barra';
export type WiredHudVisao = 'pessoal' | 'oficial' | 'ambos';
export type WiredHudCategoria = 'status' | 'combate' | 'medicamentos' | 'suprimentos' | 'outros';

export const WIRED_HUD_CATEGORIAS: { value: WiredHudCategoria; label: string }[] = [
  { value: 'status', label: 'Status do jogador' },
  { value: 'combate', label: 'Inventário de combate' },
  { value: 'medicamentos', label: 'Inventário - medicamentos' },
  { value: 'suprimentos', label: 'Inventário - suprimentos' },
  { value: 'outros', label: 'Outros' },
];

export const WIRED_HUD_FORMATOS: { value: WiredHudFormato; label: string; ajuda: string }[] = [
  { value: 'numero', label: 'Número', ajuda: 'Mostra o valor como está.' },
  { value: 'texto_por_valor', label: 'Texto por valor', ajuda: 'Troca cada valor por um texto (ex.: 1 = Ativo).' },
  { value: 'booleano', label: 'Sim / Não', ajuda: '0 aparece como Não; qualquer outro valor como Sim.' },
  { value: 'tempo', label: 'Tempo', ajuda: 'O valor é uma duração em segundos (ex.: 3h 05min).' },
  { value: 'barra', label: 'Barra', ajuda: 'Barra de progresso: valor atual (esta variável) e limite máximo (outra variável ou um número fixo).' },
];

export const WIRED_HUD_VISOES: { value: WiredHudVisao; label: string }[] = [
  { value: 'pessoal', label: 'Pessoal (cada militar vê a própria)' },
  { value: 'oficial', label: 'Oficiais (lista de militares)' },
  { value: 'ambos', label: 'Pessoal e oficiais' },
];

// Mesma lista de ícones suportada pelo app do HUD.
export const WIRED_HUD_ICONES = [
  'shield', 'star', 'medal', 'clock', 'user', 'flag', 'heart', 'zap', 'trophy', 'target', 'crown', 'award',
];

export interface WiredHudVariavel {
  id: string;
  id_conexao: string;
  conexao_nome: string | null;
  nome_variavel: string;
  rotulo: string;
  icone: string | null;
  categoria: WiredHudCategoria;
  formato: WiredHudFormato;
  mapa_valores: Record<string, string> | null;
  max_nome_variavel: string | null;
  max_valor_fixo: string | null;
  id_acao: string | null;
  visao: WiredHudVisao;
  ordem: number;
  ativo: boolean;
  alterado_em: string;
}

export interface WiredHudVariavelPayload {
  id_conexao?: string;
  nome_variavel?: string;
  rotulo?: string;
  icone?: string | null;
  categoria?: WiredHudCategoria;
  formato?: WiredHudFormato;
  mapa_valores?: Record<string, string> | null;
  max_nome_variavel?: string | null;
  max_valor_fixo?: string | null;
  id_acao?: string | null;
  visao?: WiredHudVisao;
  ordem?: number;
  ativo?: boolean;
}

export interface WiredHudAcao {
  id: string;
  id_conexao: string;
  conexao_nome: string | null;
  rotulo: string;
  icone: string | null;
  nome_variavel_disparo: string;
  valor_disparo: string;
  nome_variavel_requisito: string | null;
  requisito_minimo: string;
  ordem: number;
  ativo: boolean;
  alterado_em: string;
}

export interface WiredHudAcaoPayload {
  id_conexao?: string;
  rotulo?: string;
  icone?: string | null;
  nome_variavel_disparo?: string;
  valor_disparo?: string;
  nome_variavel_requisito?: string | null;
  requisito_minimo?: string;
  ordem?: number;
  ativo?: boolean;
}

export type WiredLojaGrupo = 'status' | 'inventario';
export type WiredLojaModo = 'incremento' | 'valor_fixo';

export const WIRED_LOJA_GRUPOS: { value: WiredLojaGrupo; label: string }[] = [
  { value: 'status', label: 'Status' },
  { value: 'inventario', label: 'Inventário' },
];

export const WIRED_LOJA_MODOS: { value: WiredLojaModo; label: string; ajuda: string }[] = [
  { value: 'incremento', label: 'Incremento', ajuda: 'Soma o valor à variável (ex.: +10 de Estamina Max).' },
  { value: 'valor_fixo', label: 'Valor fixo', ajuda: 'Grava o valor na variável (ex.: Arma = 3).' },
];

export interface WiredLojaConfig {
  id: string;
  id_conexao: string;
  conexao_nome: string | null;
  nome_variavel_pontos: string;
  nome_variavel_elegibilidade: string | null;
  ativo: boolean;
}

export interface WiredLojaConfigPayload {
  id_conexao: string;
  nome_variavel_pontos: string;
  nome_variavel_elegibilidade: string | null;
  ativo: boolean;
}

export const WIRED_LOJA_OPERADORES = [
  { value: '>=', label: 'maior ou igual a (≥)' },
  { value: '<=', label: 'menor ou igual a (≤)' },
  { value: '>', label: 'maior que (>)' },
  { value: '<', label: 'menor que (<)' },
  { value: '==', label: 'igual a (=)' },
  { value: '!=', label: 'diferente de (≠)' },
] as const;

export type WiredLojaOperador = (typeof WIRED_LOJA_OPERADORES)[number]['value'];

export interface WiredLojaRequisito {
  tipo?: 'variavel' | 'nivel';
  rotulo: string;
  nome_variavel: string;
  operador: WiredLojaOperador;
  valor: string;
}

export interface WiredLojaCatalogoRequisito {
  tipo?: 'variavel' | 'nivel';
  rotulo: string;
  operador: WiredLojaOperador;
  valor: string;
  atual: string;
  atendido: boolean;
}

export type WiredMissaoPrazoTipo = 'nenhum' | 'relativo' | 'fixo';
export type WiredMissaoObjetivoModo = 'delta' | 'retroativo';
export type WiredMissaoStatus = 'em_andamento' | 'concluida' | 'expirada' | 'abandonada';

export interface WiredMissaoObjetivo {
  id?: string;
  rotulo: string;
  nome_variavel: string;
  meta: string;
  modo: WiredMissaoObjetivoModo;
  obrigatorio?: boolean;
  id_conexao?: string | null;
}

export interface WiredMissaoRecompensa {
  rotulo: string;
  nome_variavel: string;
  valor: string;
  teto_valor: string | null;
  id_conexao?: string | null;
}

export interface WiredMissao {
  id: string;
  id_conexao: string;
  nome: string;
  descricao: string | null;
  imagem_url: string | null;
  prazo_tipo: WiredMissaoPrazoTipo;
  prazo_horas: number | null;
  prazo_ate: string | null;
  repetivel: boolean;
  cooldown_horas: number | null;
  qtd_sorteio: number | null;
  limite_simultaneas: number | null;
  objetivos: WiredMissaoObjetivo[];
  requisitos: WiredLojaRequisito[];
  recompensas: WiredMissaoRecompensa[];
  ordem: number;
  ativo: boolean;
}

export type WiredMissaoPayload = Partial<Omit<WiredMissao, 'id'>>;

export interface WiredEstatistica {
  id: string;
  id_conexao: string;
  conexao_nome: string | null;
  nome_variavel: string;
  rotulo: string;
  categoria: string;
  ordem: number;
  ativo: boolean;
}

export type WiredEstatisticaPayload = Partial<Omit<WiredEstatistica, 'id' | 'conexao_nome' | 'ordem'>>;

export interface WiredMinhasEstatisticas {
  categorias: { nome: string; itens: { id: string; rotulo: string; valor: string }[] }[];
}

export interface WiredMissaoRegistroAdmin {
  id: string;
  militar: string | null;
  status: WiredMissaoStatus;
  aceita_em: string;
  prazo_em: string | null;
  finalizada_em: string | null;
}

export interface WiredMissaoResumo {
  id: string;
  nome: string;
  descricao: string | null;
  imagem_url: string | null;
  recompensas: { rotulo: string; valor: string }[];
}

export interface WiredMissaoDisponivel extends WiredMissaoResumo {
  prazo_tipo: WiredMissaoPrazoTipo;
  prazo_horas: number | null;
  prazo_ate: string | null;
  repetivel: boolean;
  cooldown_horas: number | null;
  total_objetivos: number;
  total_obrigatorios: number;
  qtd_sorteio: number | null;
  objetivos: { rotulo: string; meta: string; modo: WiredMissaoObjetivoModo; obrigatorio: boolean }[];
  requisitos: WiredLojaCatalogoRequisito[];
  disponivel_em: string | null;
  pode_aceitar: boolean;
  motivo: string | null;
}

export interface WiredMissaoAndamento extends WiredMissaoResumo {
  id_missao: string;
  aceita_em: string;
  prazo_em: string | null;
  objetivos: { rotulo: string; modo: WiredMissaoObjetivoModo; meta: string; atual: string; concluido: boolean }[];
  pode_concluir: boolean;
}

export interface WiredMissaoHistorico {
  id: string;
  nome: string;
  imagem_url: string | null;
  status: WiredMissaoStatus;
  aceita_em: string;
  finalizada_em: string | null;
}

export interface WiredMinhasMissoes {
  limite_simultaneas: number | null;
  em_andamento_total: number;
  disponiveis: WiredMissaoDisponivel[];
  em_andamento: WiredMissaoAndamento[];
  historico: WiredMissaoHistorico[];
}

export type WiredNivelModo = 'linear' | 'exponencial' | 'manual';

export const WIRED_NIVEL_MODOS: { valor: WiredNivelModo; rotulo: string }[] = [
  { valor: 'linear', rotulo: 'Linear' },
  { valor: 'exponencial', rotulo: 'Exponencial' },
  { valor: 'manual', rotulo: 'Entrada manual' },
];

export interface WiredNivelRecompensa {
  id?: string;
  rotulo: string;
  nome_variavel: string;
  valor: string;
  a_cada: number;
  a_partir_de: number;
  teto_valor: string | null;
  id_conexao?: string | null;
  pagar_retroativo?: boolean;
}

export interface WiredNivel {
  id: string;
  id_conexao: string;
  nome: string;
  icone: string | null;
  nome_variavel_xp: string;
  modo: WiredNivelModo;
  passo: number | null;
  xp_base: number | null;
  fator: number | null;
  nivel_maximo: number | null;
  entrada_manual: string | null;
  nivel_maximo_efetivo: number;
  xp_maximo: number;
  recompensas: WiredNivelRecompensa[];
  retroativo: boolean;
  ordem: number;
  ativo: boolean;
  alterado_em: string;
}

export interface WiredNivelPayload {
  id_conexao?: string;
  nome?: string;
  icone?: string | null;
  nome_variavel_xp?: string;
  modo?: WiredNivelModo;
  passo?: number | null;
  xp_base?: number | null;
  fator?: number | null;
  nivel_maximo?: number | null;
  entrada_manual?: string | null;
  recompensas?: WiredNivelRecompensa[];
  retroativo?: boolean;
  ordem?: number;
  ativo?: boolean;
}

export interface WiredNivelInfo {
  nivel: number;
  xp: number;
  progresso: number;
  xp_necessario: number;
  xp_restante: number;
  percentual: number;
  no_maximo: boolean;
  nivel_maximo: number;
  xp_maximo: number;
}

export interface WiredMeuNivel extends WiredNivelInfo {
  id: string;
  nome: string;
  icone: string | null;
  sem_perfil: boolean;
  recompensas: { rotulo: string; valor: string; a_cada: number; a_partir_de: number }[];
  recompensa_pendente: boolean;
}

export interface WiredNivelResgate {
  nome: string;
  de_nivel: number;
  ate_nivel: number;
  ganhos: { rotulo: string; quantidade: string }[];
}

export interface WiredLojaEfeitoExtra {
  rotulo: string;
  nome_variavel: string;
  modo: WiredLojaModo;
  valor: string;
  teto_valor: string | null;
}

export interface WiredLojaItem {
  id: string;
  id_conexao: string;
  grupo: WiredLojaGrupo;
  rotulo: string;
  icone: string | null;
  modo: WiredLojaModo;
  nome_variavel_alvo: string;
  valor: string;
  custo: string;
  teto_valor: string | null;
  efeitos_extras: WiredLojaEfeitoExtra[];
  requisitos: WiredLojaRequisito[];
  limite_compras: number | null;
  vendidas: number;
  ordem: number;
  ativo: boolean;
  alterado_em: string;
}

export interface WiredLojaItemPayload {
  id_conexao?: string;
  grupo?: WiredLojaGrupo;
  rotulo?: string;
  icone?: string | null;
  modo?: WiredLojaModo;
  nome_variavel_alvo?: string;
  valor?: string;
  custo?: string;
  teto_valor?: string | null;
  efeitos_extras?: WiredLojaEfeitoExtra[];
  requisitos?: WiredLojaRequisito[];
  limite_compras?: number | null;
  ordem?: number;
  ativo?: boolean;
}

export interface WiredLojaCatalogoEfeito {
  rotulo: string;
  modo: WiredLojaModo;
  valor: string;
  teto_valor: string | null;
  atual: string;
}

export interface WiredLojaCatalogoItem {
  id: string;
  grupo: WiredLojaGrupo;
  rotulo: string;
  icone: string | null;
  modo: WiredLojaModo;
  valor: string;
  custo: string;
  teto_valor: string | null;
  efeitos_extras: WiredLojaCatalogoEfeito[];
  requisitos: WiredLojaCatalogoRequisito[];
  atual: string;
  limite_compras: number | null;
  restantes: number | null;
}

export interface WiredLojaCatalogo {
  disponivel: boolean;
  elegivel?: boolean;
  pontos?: string;
  itens?: WiredLojaCatalogoItem[];
}

export interface WiredHudValor {
  id: string;
  rotulo: string;
  icone: string | null;
  categoria: 'status' | 'combate' | 'medicamentos' | 'suprimentos' | 'outros';
  formato: string;
  valor: string | null;
  texto: string;
  maximo: string | null;
  percentual: number | null;
}

export interface WiredHudMeuPerfil {
  nick: string;
  patente: string | null;
  sem_perfil: boolean;
  atualizado_em: string;
  variaveis: WiredHudValor[];
}

export interface WiredLojaCompraResposta {
  ok: boolean;
  mensagem: string;
  pontos: string;
  valor: string;
}

export interface WiredSincronizacoesFiltro {
  status?: string;
  codigo?: string;
  nick?: string;
  page?: number;
  size?: number;
}

export interface WiredSincronizacoesResposta {
  items: WiredSincronizacao[];
  total: number;
  page: number;
  size: number;
  resumo: WiredResumo;
}

export const wiredService = {
  async listarConexoes(): Promise<WiredConexao[]> {
    const response = await api.get<WiredConexao[]>('/wired/conexoes');
    return response.data;
  },

  async criarConexao(payload: WiredConexaoPayload): Promise<WiredConexao> {
    const response = await api.post<WiredConexao>('/wired/conexoes', payload);
    return response.data;
  },

  async atualizarConexao(id: string, payload: WiredConexaoPayload): Promise<WiredConexao> {
    const response = await api.put<WiredConexao>(`/wired/conexoes/${id}`, payload);
    return response.data;
  },

  async excluirConexao(id: string): Promise<void> {
    await api.delete(`/wired/conexoes/${id}`);
  },

  async testarConexao(id: string): Promise<WiredTesteConexaoResposta> {
    const response = await api.post<WiredTesteConexaoResposta>(`/wired/conexoes/${id}/testar`);
    return response.data;
  },

  async listarVariaveisConexao(id: string): Promise<{ ok: boolean; erro?: string; usuario: string[] }> {
    const response = await api.get<{ ok: boolean; erro?: string; usuario: string[] }>(
      `/wired/conexoes/${id}/variaveis`
    );
    return response.data;
  },

  async listarFuncionalidades(): Promise<WiredFuncionalidade[]> {
    const response = await api.get<WiredFuncionalidade[]>('/wired/funcionalidades');
    return response.data;
  },

  async atualizarFuncionalidade(
    id: string,
    payload: WiredFuncionalidadePayload
  ): Promise<WiredFuncionalidade> {
    const response = await api.put<WiredFuncionalidade>(`/wired/funcionalidades/${id}`, payload);
    return response.data;
  },

  async listarSincronizacoes(
    filtro: WiredSincronizacoesFiltro = {}
  ): Promise<WiredSincronizacoesResposta> {
    const params: Record<string, string | number> = {};
    if (filtro.status) params.status = filtro.status;
    if (filtro.codigo) params.codigo = filtro.codigo;
    if (filtro.nick) params.nick = filtro.nick;
    if (filtro.page) params.page = filtro.page;
    if (filtro.size) params.size = filtro.size;
    const response = await api.get<WiredSincronizacoesResposta>('/wired/sincronizacoes', { params });
    return response.data;
  },

  async reenviarSincronizacao(id: string): Promise<void> {
    await api.post(`/wired/sincronizacoes/${id}/reenviar`);
  },

  async listarHudVariaveis(): Promise<WiredHudVariavel[]> {
    const response = await api.get<WiredHudVariavel[]>('/wired/hud/variaveis');
    return response.data;
  },

  async criarHudVariavel(payload: WiredHudVariavelPayload): Promise<WiredHudVariavel> {
    const response = await api.post<WiredHudVariavel>('/wired/hud/variaveis', payload);
    return response.data;
  },

  async atualizarHudVariavel(id: string, payload: WiredHudVariavelPayload): Promise<WiredHudVariavel> {
    const response = await api.put<WiredHudVariavel>(`/wired/hud/variaveis/${id}`, payload);
    return response.data;
  },

  async excluirHudVariavel(id: string): Promise<void> {
    await api.delete(`/wired/hud/variaveis/${id}`);
  },

  async listarHudAcoes(): Promise<WiredHudAcao[]> {
    const response = await api.get<WiredHudAcao[]>('/wired/hud/acoes');
    return response.data;
  },

  async criarHudAcao(payload: WiredHudAcaoPayload): Promise<WiredHudAcao> {
    const response = await api.post<WiredHudAcao>('/wired/hud/acoes', payload);
    return response.data;
  },

  async atualizarHudAcao(id: string, payload: WiredHudAcaoPayload): Promise<WiredHudAcao> {
    const response = await api.put<WiredHudAcao>(`/wired/hud/acoes/${id}`, payload);
    return response.data;
  },

  async excluirHudAcao(id: string): Promise<void> {
    await api.delete(`/wired/hud/acoes/${id}`);
  },

  async listarLojaConfig(): Promise<WiredLojaConfig[]> {
    const response = await api.get<WiredLojaConfig[]>('/wired/loja/config');
    return response.data;
  },

  async salvarLojaConfig(payload: WiredLojaConfigPayload): Promise<WiredLojaConfig> {
    const response = await api.put<WiredLojaConfig>('/wired/loja/config', payload);
    return response.data;
  },

  async listarLojaItens(): Promise<WiredLojaItem[]> {
    const response = await api.get<WiredLojaItem[]>('/wired/loja/itens');
    return response.data;
  },

  async criarLojaItem(payload: WiredLojaItemPayload): Promise<WiredLojaItem> {
    const response = await api.post<WiredLojaItem>('/wired/loja/itens', payload);
    return response.data;
  },

  async atualizarLojaItem(id: string, payload: WiredLojaItemPayload): Promise<WiredLojaItem> {
    const response = await api.put<WiredLojaItem>(`/wired/loja/itens/${id}`, payload);
    return response.data;
  },

  async excluirLojaItem(id: string): Promise<void> {
    await api.delete(`/wired/loja/itens/${id}`);
  },

  async obterLoja(): Promise<WiredLojaCatalogo> {
    const response = await api.get<WiredLojaCatalogo>('/wired/loja');
    return response.data;
  },

  async comprarLojaItem(id: string): Promise<WiredLojaCompraResposta> {
    const response = await api.post<WiredLojaCompraResposta>(`/wired/loja/itens/${id}/comprar`);
    return response.data;
  },

  async obterAcessoSobrevivente(): Promise<boolean> {
    const response = await api.get<{ acesso: boolean }>('/wired/sobrevivente/acesso');
    return response.data.acesso;
  },

  async obterHudMeuPerfil(): Promise<WiredHudMeuPerfil> {
    const response = await api.get<WiredHudMeuPerfil>('/wired/hud/meu-perfil');
    return response.data;
  },

  async listarNiveis(): Promise<WiredNivel[]> {
    const response = await api.get<WiredNivel[]>('/wired/niveis');
    return response.data;
  },

  async criarNivel(payload: WiredNivelPayload): Promise<WiredNivel> {
    const response = await api.post<WiredNivel>('/wired/niveis', payload);
    return response.data;
  },

  async atualizarNivel(id: string, payload: WiredNivelPayload): Promise<WiredNivel> {
    const response = await api.put<WiredNivel>(`/wired/niveis/${id}`, payload);
    return response.data;
  },

  async excluirNivel(id: string): Promise<void> {
    await api.delete(`/wired/niveis/${id}`);
  },

  async simularNivel(payload: WiredNivelPayload & { xp: string }): Promise<WiredNivelInfo> {
    const response = await api.post<WiredNivelInfo>('/wired/niveis/simular', payload);
    return response.data;
  },

  async obterMeusNiveis(): Promise<{ niveis: WiredMeuNivel[] }> {
    const response = await api.get<{ niveis: WiredMeuNivel[] }>('/wired/niveis/meus');
    return response.data;
  },

  async resgatarRecompensasNivel(): Promise<{ resgates: WiredNivelResgate[] }> {
    const response = await api.post<{ resgates: WiredNivelResgate[] }>('/wired/niveis/resgatar');
    return response.data;
  },

  async listarMissoes(): Promise<WiredMissao[]> {
    const response = await api.get<WiredMissao[]>('/wired/missoes');
    return response.data;
  },

  async criarMissao(payload: WiredMissaoPayload): Promise<WiredMissao> {
    const response = await api.post<WiredMissao>('/wired/missoes', payload);
    return response.data;
  },

  async atualizarMissao(id: string, payload: WiredMissaoPayload): Promise<WiredMissao> {
    const response = await api.put<WiredMissao>(`/wired/missoes/${id}`, payload);
    return response.data;
  },

  async excluirMissao(id: string): Promise<void> {
    await api.delete(`/wired/missoes/${id}`);
  },

  async obterConfigMissoes(): Promise<{ limite_simultaneas: number | null }> {
    const response = await api.get<{ limite_simultaneas: number | null }>('/wired/missoes/config');
    return response.data;
  },

  async salvarConfigMissoes(limite: number | null): Promise<{ limite_simultaneas: number | null }> {
    const response = await api.put<{ limite_simultaneas: number | null }>('/wired/missoes/config', {
      limite_simultaneas: limite,
    });
    return response.data;
  },

  async listarRegistrosMissao(id: string): Promise<WiredMissaoRegistroAdmin[]> {
    const response = await api.get<WiredMissaoRegistroAdmin[]>(`/wired/missoes/${id}/registros`);
    return response.data;
  },

  async obterMinhasMissoes(): Promise<WiredMinhasMissoes> {
    const response = await api.get<WiredMinhasMissoes>('/wired/missoes/minhas');
    return response.data;
  },

  async aceitarMissao(id: string): Promise<void> {
    await api.post(`/wired/missoes/${id}/aceitar`);
  },

  async concluirMissao(idRegistro: string): Promise<{ ok: true; ganhos: { rotulo: string; quantidade: string }[] }> {
    const response = await api.post<{ ok: true; ganhos: { rotulo: string; quantidade: string }[] }>(
      `/wired/missoes/registros/${idRegistro}/concluir`
    );
    return response.data;
  },

  async abandonarMissao(idRegistro: string): Promise<void> {
    await api.post(`/wired/missoes/registros/${idRegistro}/abandonar`);
  },

  async reordenar(
    recurso: 'hud/variaveis' | 'hud/acoes' | 'loja/itens' | 'niveis' | 'missoes' | 'estatisticas',
    ids: string[]
  ): Promise<void> {
    await api.put(`/wired/${recurso}/ordem`, { ids });
  },

  async listarEstatisticas(): Promise<WiredEstatistica[]> {
    const response = await api.get<WiredEstatistica[]>('/wired/estatisticas');
    return response.data;
  },

  async criarEstatistica(payload: WiredEstatisticaPayload): Promise<WiredEstatistica> {
    const response = await api.post<WiredEstatistica>('/wired/estatisticas', payload);
    return response.data;
  },

  async atualizarEstatistica(id: string, payload: WiredEstatisticaPayload): Promise<WiredEstatistica> {
    const response = await api.put<WiredEstatistica>(`/wired/estatisticas/${id}`, payload);
    return response.data;
  },

  async excluirEstatistica(id: string): Promise<void> {
    await api.delete(`/wired/estatisticas/${id}`);
  },

  async obterMinhasEstatisticas(): Promise<WiredMinhasEstatisticas> {
    const response = await api.get<WiredMinhasEstatisticas>('/wired/estatisticas/minhas');
    return response.data;
  },
};
